import { Icon } from '@livable/saas-react-components';
import { Chip, Typography } from '@mui/material';
import moment from 'moment';
import React, { useMemo } from 'react';
import { BillAutomationExtractedEditButton } from './BillAutomationExtractedEditButton';
import { useBillAutomationExtractedStyles } from './billAutomationExtractedStyles';
import { getExtractedTotalAmount } from './billAutomationExtractionUtils';
export function formatUtilityLabel(serviceType) {
  if (!serviceType) return '';
  const s = String(serviceType).replace(/_/g, ' ');
  return s.replace(/\b\w/g, c => c.toUpperCase());
}
export function formatExtractedMoney(amount) {
  let currency = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'USD';
  if (amount === undefined || amount === null || Number.isNaN(Number(amount))) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(Number(amount));
  } catch (_unused) {
    return "$".concat(Number(amount).toFixed(2));
  }
}

/** Map API service type strings to Livable Icon `type` names. */
export function getUtilityIconType(serviceType) {
  const raw = String(serviceType || '').toLowerCase().replace(/_/g, ' ');
  if (raw.includes('electric')) return 'electricity';
  if (raw.includes('water') && raw.includes('sewer')) return 'water/sewer';
  if (raw.includes('sewer')) return 'sewer';
  if (raw.includes('water')) return 'water';
  if (raw.includes('trash') || raw.includes('garbage') || raw.includes('waste') || raw.includes('sanitation')) return 'trash';
  if (raw.includes('gas')) return 'natural-gas';
  if (raw.includes('irrigation')) return 'irrigation';
  if (raw.includes('steam')) return 'steam';
  if (raw.includes('solar')) return 'solar';
  if (raw.includes('property') || raw.includes('rent')) return 'property';
  return 'meter';
}
export function BillAutomationExtractedSummary(_ref) {
  var _summary$finalValidat, _summary$finalValidat2;
  let customerName = _ref.customerName,
    data = _ref.data,
    fileName = _ref.fileName,
    status = _ref.status,
    utilityTypeName = _ref.utilityTypeName;
  const classes = useBillAutomationExtractedStyles();
  const summary = useMemo(() => {
    var _data$accountNumber;
    if (!data) return null;
    const utilities = new Set();
    (data.properties || []).forEach(p => {
      (p.services || []).forEach(s => {
        if (s.serviceType) utilities.add(s.serviceType);
      });
    });
    const utilityList = [...utilities];
    const addresses = (data.properties || []).map(p => p.address).filter(Boolean);
    return {
      provider: data.vendorName || '—',
      accountNumber: (_data$accountNumber = data.accountNumber) !== null && _data$accountNumber !== void 0 ? _data$accountNumber : '—',
      billDate: data.billDate ? moment(data.billDate).format('MM/DD/YYYY') : '—',
      dueDate: data.dueDate ? moment(data.dueDate).format('MM/DD/YYYY') : '—',
      totalDue: formatExtractedMoney(getExtractedTotalAmount(data, utilityTypeName), data.currency),
      utilityList,
      addresses,
      finalValidation: data.finalValidation,
      parseConfidence: data.parseConfidence,
      notes: data.notes
    };
  }, [data, utilityTypeName]);
  if (!data || !summary) return null;
  const verified = ((_summary$finalValidat = summary.finalValidation) === null || _summary$finalValidat === void 0 ? void 0 : _summary$finalValidat.isValid) === true;
  const validationReason = ((_summary$finalValidat2 = summary.finalValidation) === null || _summary$finalValidat2 === void 0 ? void 0 : _summary$finalValidat2.reason) || (verified ? 'Provider, utilities, and address match with existing property records.' : 'Some validation checks did not pass. Review extracted values before continuing.');
  const statusLabel = status && String(status).toLowerCase() === 'extracted' ? 'Extracted' : status || '';
  return /*#__PURE__*/React.createElement("div", {
    className: classes.wrapper
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.fileStrip
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.fileStripLeft
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "grey",
    type: "file-text"
  }), /*#__PURE__*/React.createElement(Typography, {
    className: classes.fileName,
    component: "span"
  }, fileName || 'Bill file'), statusLabel ? /*#__PURE__*/React.createElement(Chip, {
    className: classes.extractedChip,
    label: statusLabel,
    size: "small"
  }) : null)), /*#__PURE__*/React.createElement("div", {
    className: classes.intro
  }, "Extraction complete. Here's what we found:", summary.parseConfidence != null ? /*#__PURE__*/React.createElement("div", {
    className: classes.introMuted
  }, "Parse confidence: ", (Number(summary.parseConfidence) * 100).toFixed(0), "%") : null), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryCard
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.cardHeader
  }, /*#__PURE__*/React.createElement(Typography, {
    className: classes.cardTitle
  }, "Bill Summary"), /*#__PURE__*/React.createElement(BillAutomationExtractedEditButton, {
    ariaLabel: "Edit bill summary"
  })), /*#__PURE__*/React.createElement("div", {
    className: classes.summaryHeaderDivider
  }), /*#__PURE__*/React.createElement("ul", {
    className: classes.summaryList
  }, /*#__PURE__*/React.createElement("li", {
    className: classes.summaryListItem
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Customer :"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.pill
  }, customerName || '—'))), /*#__PURE__*/React.createElement("li", {
    className: classes.summaryListItem
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Provider :"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.pill
  }, summary.provider))), /*#__PURE__*/React.createElement("li", {
    className: classes.summaryListItem
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Utilities :"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.utilityChips
  }, summary.utilityList.length ? summary.utilityList.map(u => /*#__PURE__*/React.createElement(Chip, {
    key: u,
    className: classes.utilityChip,
    label: formatUtilityLabel(u),
    size: "small"
  })) : '—'))), /*#__PURE__*/React.createElement("li", {
    className: classes.summaryListItem
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Properties :"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.propertyPills
  }, summary.addresses.length ? summary.addresses.map((addr, i) => /*#__PURE__*/React.createElement(Chip, {
    key: "".concat(addr, "-").concat(i),
    className: classes.propertyPill,
    label: addr,
    size: "small"
  })) : '—')))), /*#__PURE__*/React.createElement("ul", {
    className: "".concat(classes.summaryList, " ").concat(classes.summaryListGroupGap)
  }, /*#__PURE__*/React.createElement("li", {
    className: classes.summaryListItem
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Account #"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValuePlain
  }, summary.accountNumber)), /*#__PURE__*/React.createElement("li", {
    className: classes.summaryListItem
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Bill date :"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValuePlain
  }, summary.billDate)), /*#__PURE__*/React.createElement("li", {
    className: classes.summaryListItem
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Due date :"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValuePlain
  }, summary.dueDate)), /*#__PURE__*/React.createElement("li", {
    className: classes.summaryListItem
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryLabel
  }, "Total due :"), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValuePlain
  }, summary.totalDue))), /*#__PURE__*/React.createElement("div", {
    className: verified ? classes.verifyBox : "".concat(classes.verifyBox, " ").concat(classes.verifyBoxWarning)
  }, verified ? /*#__PURE__*/React.createElement(Icon, {
    type: "check-circle"
  }) : /*#__PURE__*/React.createElement(Icon, {
    state: "warning",
    type: "warning"
  }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Typography, {
    className: verified ? classes.verifyTitle : "".concat(classes.verifyTitle, " ").concat(classes.verifyTitleWarn)
  }, verified ? 'Match: Verified' : 'Review recommended'), /*#__PURE__*/React.createElement(Typography, {
    className: "".concat(classes.verifyBody, " ").concat(verified ? classes.verifyBodyVerified : classes.verifyBodyWarning)
  }, validationReason))), summary.notes ? /*#__PURE__*/React.createElement(Typography, {
    className: classes.notes
  }, /*#__PURE__*/React.createElement("strong", null, "Notes:"), " ", summary.notes) : null));
}